import React, { useState, useEffect, useRef } from 'react';
import { Settings as SettingsIcon, Bell, User, Search, Clock, Info, Shield, 
  Wallet, Copy, CheckCircle, ExternalLink, X, Twitter, MessageSquare, Save, 
  Loader, AlertCircle, Lock, Upload, Image } from 'lucide-react';
import { getUserProfile, UserProfile, saveUserProfile, updateNotificationPreferences, 
  getNotificationPreferences, canChangeDisplayName, notifyDisplayNameChange } from '../services/userProfileService';
import { checkSupabaseConnection } from '../utils/supabaseDebug';
import { detectConnectedWallet } from '../utils/walletDetection';
import { supabase, adminSupabase } from '../services/supabaseService';

// Custom styles for Settings component
const settingsStyles = `
  /* Custom scrollbar styling */
  .settings-scrollbar::-webkit-scrollbar {
    width: 6px;
  }

  .settings-scrollbar::-webkit-scrollbar-track {
    background: rgba(30, 40, 40, 0.2);
    border-radius: 3px;
  }

  .settings-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(71, 190, 143, 0.3);
    border-radius: 3px;
    transition: all 0.2s;
  }

  .settings-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(71, 190, 143, 0.5);
  }
  
  /* Section styles with solid backgrounds */
  .settings-card {
    background: #182828;
    border: 1px solid rgba(71, 190, 143, 0.2);
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    overflow: hidden;
  }
  
  .settings-card-header {
    background: #1a2a2a;
    border-bottom: 1px solid rgba(71, 190, 143, 0.2);
    padding: 0.75rem 1rem;
  }
  
  .settings-card-body {
    padding: 1rem;
  }
  
  /* Form element styling */
  .settings-input {
    background: #1c2c2c;
    border: 1px solid rgba(71, 190, 143, 0.2);
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
    color: white;
    width: 100%;
    transition: all 0.2s;
  }
  
  .settings-input:focus {
    outline: none;
    border-color: rgba(71, 190, 143, 0.5);
    box-shadow: 0 0 0 1px rgba(71, 190, 143, 0.2);
  }
  
  .settings-toggle {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 22px;
  }
  
  .settings-toggle input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .toggle-slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #1c2c2c;
    border: 1px solid rgba(71, 190, 143, 0.2);
    transition: .4s;
    border-radius: 22px;
  }
  
  .toggle-slider:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  
  input:checked + .toggle-slider {
    background-color: rgba(71, 190, 143, 0.7);
    border-color: rgba(71, 190, 143, 0.8);
  }
  
  input:checked + .toggle-slider:before {
    transform: translateX(22px);
  }
  
  /* Button styling */
  .settings-button-primary {
    background: #42ae84;
    color: #182828;
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-weight: 500;
    transition: all 0.2s;
  }
  
  .settings-button-primary:hover {
    background: rgba(66, 174, 132, 0.9);
  }
  
  .settings-button-secondary {
    background: rgba(66, 174, 132, 0.1);
    color: #42ae84;
    border: 1px solid rgba(66, 174, 132, 0.2);
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-weight: 500;
    transition: all 0.2s;
  }
  
  .settings-button-secondary:hover {
    background: rgba(66, 174, 132, 0.2);
  }
  
  /* Labels and field spacing */
  .field-group {
    margin-bottom: 0.75rem;
  }
  
  .field-group:last-child {
    margin-bottom: 0;
  }
  
  /* Character counter styles */
  .char-counter {
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    font-size: 0.75rem;
    color: #a0aec0;
  }
  
  .char-counter.warning {
    color: #f59e0b;
  }
  
  /* No margin between fields */
  .compact-form label {
    margin-bottom: 0.25rem;
    display: block;
  }
  
  .compact-form .text-xs {
    margin-top: 0.25rem;
  }
`;

// Add CSS for the avatar upload component
const avatarStyles = `
  .avatar-upload {
    position: relative;
    width: 110px;
    height: 110px;
    cursor: pointer;
    margin-right: 15px;
  }
  
  .avatar-upload-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
  }
  
  .avatar-upload:hover .avatar-upload-overlay {
    opacity: 1;
  }
  
  .avatar-upload-input {
    display: none;
  }
  
  .avatar-preview {
    width: 100%;
    height: 100%;
    border-radius: 8px;
    object-fit: cover;
    border: 1px solid rgba(71, 190, 143, 0.2);
    background: #1c2c2c;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    padding: 8px;
    text-align: center;
  }
  
  .avatar-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 7px;
  }

  .avatar-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
  }
  
  .avatar-placeholder-info {
    font-size: 8px;
    color: #a0aec0;
    margin-top: 4px;
    line-height: 1.2;
  }
  
  .avatar-upload-progress {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: white;
  }
  
  .avatar-drop-active {
    border: 2px dashed #42ae84;
    background: rgba(66, 174, 132, 0.1);
  }
`;

interface SettingsProps {
  onShowConnectModal?: () => void;
}

const formatWalletAddress = (address: string | undefined) => {
  if (!address) return '';
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

// Helper function to format time remaining
const formatTimeRemaining = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
};

const Settings: React.FC<SettingsProps> = ({ onShowConnectModal }) => {
  const [activeTab, setActiveTab] = useState<'account' | 'notifications'>('account');
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [walletProvider, setWalletProvider] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileUpdated, setProfileUpdated] = useState(false);
  
  // Display name change state
  const [canChangeName, setCanChangeName] = useState(true);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [nextChangeDate, setNextChangeDate] = useState<Date | null>(null);
  const [originalDisplayName, setOriginalDisplayName] = useState<string | null>(null);
  
  // Avatar upload state
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Profile state
  const [profile, setProfile] = useState<UserProfile>({
    display_name: '',
    email: '',
    twitter_handle: '',
    telegram_handle: '',
    discord_handle: '',
    bio: '',
    avatar_url: null,
    last_display_name_change: null,
    active_trade_wallet: null
  });
  
  // Notification preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    trade_notifications: true,
    price_alerts: true,
    wallet_activity: true,
    gas_price_alerts: false,
    smart_contract_alerts: false,
    in_app_notifications: true,
    email_notifications: false,
    push_notifications: false
  });

  // Add state for character count
  const [bioCharCount, setBioCharCount] = useState(0);

  // Add timer effect to update the countdown
  useEffect(() => {
    if (timeLeft && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev === null || prev <= 1000) return null;
          return prev - 1000;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [timeLeft]);

  // Check for wallet connection on component mount
  useEffect(() => {
    const checkWalletConnection = async () => {
      setIsLoading(true);
      try {
        // Use our utility to detect wallet connection
        const wallet = detectConnectedWallet();
        
        if (wallet) {
          setWalletAddress(wallet.address);
          setWalletProvider(wallet.provider);
          await loadUserData(wallet.address);
        } else {
          setWalletAddress(null);
          setWalletProvider(null);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Error checking wallet connection:", err);
        setError("Failed to connect to wallet. Please refresh and try again.");
        setIsLoading(false);
      }
    };
    
    checkWalletConnection();
    
    // Debug Supabase connection
    checkSupabaseConnection();
  }, []);

  // Improve the checkDisplayNameChange function to force timer update
  const checkDisplayNameChange = async (address: string) => {
    try {
      const { canChange, timeLeft, nextChangeDate } = await canChangeDisplayName(address);
      setCanChangeName(canChange);
      
      // Force timer to restart if there's still time left
      if (timeLeft && timeLeft > 0) {
        // Clear any existing timer interval by setting timeLeft to null first
        setTimeLeft(null);
        
        // Use setTimeout to ensure the state is updated properly before setting new timeLeft
        setTimeout(() => {
          setTimeLeft(timeLeft);
          setNextChangeDate(nextChangeDate || null);
        }, 0);
      } else {
        setTimeLeft(null);
        setNextChangeDate(null);
      }
    } catch (error) {
      console.error("Error checking display name change:", error);
      // Default to allowing changes if there's an error
      setCanChangeName(true);
      setTimeLeft(null);
      setNextChangeDate(null);
    }
  };

  // Load user data from Supabase
  const loadUserData = async (address: string) => {
    try {
      // Get user profile
      const userProfile = await getUserProfile(address);
      if (userProfile) {
        
        // Set avatar URL for preview first to ensure it's visible
        setAvatarUrl(userProfile.avatar_url || null);
        
        // Update profile state
        setProfile({
          display_name: userProfile.display_name || '',
          email: userProfile.email || '',
          twitter_handle: userProfile.twitter_handle || '',
          telegram_handle: userProfile.telegram_handle || '',
          discord_handle: userProfile.discord_handle || '',
          bio: userProfile.bio || '',
          avatar_url: userProfile.avatar_url || null,
          last_display_name_change: userProfile.last_display_name_change || null,
          active_trade_wallet: userProfile.active_trade_wallet || null
        });
        
        // Save original display name to detect changes
        setOriginalDisplayName(userProfile.display_name || null);
        
        // Check if user can change display name
        await checkDisplayNameChange(address);
        
        console.log("Profile state updated with avatar URL:", userProfile.avatar_url);
      } else {
        console.warn("No user profile found for address:", address);
      }
      
      // Get notification preferences
      const prefs = await getNotificationPreferences(address);
      if (prefs) {
        setNotificationPrefs(prevState => ({
          ...prevState,
          ...prefs
        }));
      }
    } catch (err) {
      console.error("Error loading user data:", err);
      setError("Failed to load your profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Set up listener for wallet connection changes
  useEffect(() => {
    const handleWalletConnect = () => {
      // Re-check wallet connection when wallet events fire
      console.log("Detected wallet connection change, updating state...");
      
      // Check if connected with Phantom
      if (window.solana?.isConnected && window.solana?.publicKey) {
        const address = window.solana.publicKey.toString();
        setWalletAddress(address);
        setWalletProvider('phantom');
        loadUserData(address);
      } 
      // Check if connected with Solflare
      else if (window.solflare?.isConnected && window.solflare?.publicKey) {
        const address = window.solflare.publicKey.toString();
        setWalletAddress(address);
        setWalletProvider('solflare');
        loadUserData(address);
      }
    };

    // Listen for wallet account changes
    window.addEventListener('walletAccountChanged', handleWalletConnect);
    
    // Add custom event listeners for your app's wallet connection events
    document.addEventListener('ekko:wallet-connected', handleWalletConnect);
    document.addEventListener('ekko:wallet-disconnected', () => {
      setWalletAddress(null);
      setWalletProvider(null);
    });

    return () => {
      window.removeEventListener('walletAccountChanged', handleWalletConnect);
      document.removeEventListener('ekko:wallet-connected', handleWalletConnect);
      document.removeEventListener('ekko:wallet-disconnected', () => {
        setWalletAddress(null);
        setWalletProvider(null);
      });
    };
  }, []);

  // Poll for wallet connections in case we missed an event
  useEffect(() => {
    // Check every 2 seconds if wallet status has changed
    const intervalId = setInterval(() => {
      // If we already have a wallet address, don't need to check
      if (walletAddress) return;
      
      // Check Phantom
      if (window.solana?.isConnected && window.solana?.publicKey) {
        const address = window.solana.publicKey.toString();
        setWalletAddress(address);
        setWalletProvider('phantom');
        loadUserData(address);
      } 
      // Check Solflare
      else if (window.solflare?.isConnected && window.solflare?.publicKey) {
        const address = window.solflare.publicKey.toString();
        setWalletAddress(address);
        setWalletProvider('solflare');
        loadUserData(address);
      }
    }, 2000);
    
    return () => clearInterval(intervalId);
  }, [walletAddress]);

  // Update the effect that checks user data on load to also reload from DB each time
  useEffect(() => {
    if (walletAddress) {
      // When coming back to the settings page, refresh user data to get updated timestamp
      getUserProfile(walletAddress).then(profile => {
        if (profile) {
          // We only care about checking for restrictions, not updating the form data
          // which is already done in loadUserData
          
          // Skip updating form data as it may erase user changes
          checkDisplayNameChange(walletAddress);
        }
      }).catch(error => {
        console.error("Error refreshing user profile on page revisit:", error);
      });
    }
  }, [walletAddress]);

  // Add a useEffect to refresh the user data when returning to the page
  useEffect(() => {
    if (walletAddress) {
      // Force a reload of the user profile when the component is mounted or revisited
      loadUserData(walletAddress);
    }
  }, [walletAddress]);

  const handleTabClick = (tab: 'account' | 'notifications') => {
    setActiveTab(tab);
  };

  const copyToClipboard = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };
  
  const handleProfileChange = (field: keyof UserProfile, value: string) => {
    // If trying to change display name but not allowed, don't update
    if (field === 'display_name' && !canChangeName) {
      return;
    }
    
    // Track character count for bio
    if (field === 'bio') {
      setBioCharCount(value.length);
    }
    
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
    setProfileUpdated(true);
  };
  
  const handleNotificationToggle = (key: string) => {
    setNotificationPrefs(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }));
  };
  
  const handleSaveProfile = async () => {
    if (!walletAddress) {
      setError("Please connect your wallet to save your profile.");
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      // Check if display name is being changed
      const displayNameChanged = originalDisplayName !== profile.display_name;
      
      if (displayNameChanged && !canChangeName) {
        setError("You can only change your display name once per week.");
        setIsSaving(false);
        return;
      }
      
      const result = await saveUserProfile(walletAddress, profile);
      
      if (result.success) {
        setSaveSuccess(true);
        setProfileUpdated(false);
        
        // If display name was changed, update state
        if (displayNameChanged) {
          // Fix by ensuring we pass string or null, never undefined
          setOriginalDisplayName(profile.display_name || null);
          setCanChangeName(false);
          
          if (result.nextChangeDate) {
            setNextChangeDate(result.nextChangeDate);
            setTimeLeft(result.timeLeft || 7 * 24 * 60 * 60 * 1000); // Default to 1 week
          }
          
          // Notify other components about the display name change
          notifyDisplayNameChange(profile.display_name || null);
        }
        
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        if (result.canChangeDisplayName === false) {
          setError("You can only change your display name once per week.");
          setCanChangeName(false);
          setTimeLeft(result.timeLeft || null);
          setNextChangeDate(result.nextChangeDate || null);
      } else {
        setError("Failed to save profile. Please try again.");
        }
      }
    } catch (err) {
      console.error("Error saving profile:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSaveNotificationPreferences = async () => {
    if (!walletAddress) {
      setError("Please connect your wallet to save your preferences.");
      return;
    }
    
    setIsSaving(true);
    setError(null);
    
    try {
      const result = await updateNotificationPreferences(walletAddress, notificationPrefs);
      
      if (result) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError("Failed to save notification preferences. Please try again.");
      }
    } catch (err) {
      console.error("Error saving notification preferences:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleConnectWallet = () => {
    if (onShowConnectModal) {
      onShowConnectModal();
    }
  };
  
  // Avatar upload handlers
  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadAvatar(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Check if the file is an image
      if (!file.type.match(/^image\/(jpeg|png|gif)$/)) {
        setError("Only JPEG, PNG and GIF files are allowed.");
        return;
      }
      
      await uploadAvatar(file);
    }
  };

  const uploadAvatar = async (file: File) => {
    if (!walletAddress) {
      setError("Please connect your wallet to upload an avatar.");
      return;
    }
    
    // Check file size - limit to 2MB
    if (file.size > 2 * 1024 * 1024) {
      setError("File size exceeds 2MB limit. Please choose a smaller file.");
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      // Create a unique file name
      const fileExt = file.name.split('.').pop();
      const fileName = `${walletAddress}-avatar-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;
      
      console.log("Uploading avatar to path:", filePath);
      
      // Use adminSupabase with service role to bypass RLS policies
      const { data, error } = await adminSupabase.storage
        .from('avatar')
        .upload(filePath, file, {
          upsert: true,
        });
        
      if (error) {
        throw error;
      }
      
      // Simulate upload progress since the onUploadProgress option isn't available
      const simulateProgress = () => {
        let progress = 0;
        const interval = setInterval(() => {
          progress += 5;
          if (progress >= 100) {
            clearInterval(interval);
            return;
          }
          setUploadProgress(progress);
        }, 100);
        
        // Clear on completion
        setTimeout(() => {
          clearInterval(interval);
          setUploadProgress(100);
        }, 2000);
      };
      
      simulateProgress();
      
      // Get public URL (also using adminSupabase)
      const { data: { publicUrl } } = adminSupabase.storage
        .from('avatar')
        .getPublicUrl(filePath);
      
      console.log("Avatar uploaded successfully. Public URL:", publicUrl);
      
      // Update profile state with new avatar URL
      setAvatarUrl(publicUrl);
      
      // Update profile with new avatar URL
      const updatedProfile = {
        ...profile,
        avatar_url: publicUrl,
        last_display_name_change: profile.last_display_name_change,
        active_trade_wallet: profile.active_trade_wallet
      };
      setProfile(updatedProfile);
      
      // Automatically save the profile to the database
      setIsSaving(true);
      console.log("Saving profile with avatar URL:", publicUrl);
      const saveResult = await saveUserProfile(walletAddress, updatedProfile);
      console.log("Profile save result:", saveResult);
      
      if (saveResult.success) {
        setSaveSuccess(true);
        setProfileUpdated(false);
        
        // Force reload user data to verify avatar URL is saved
        console.log("Reloading user data after avatar upload...");
        await loadUserData(walletAddress);
        
        // Use a timeout to log the new profile state after it's updated
        setTimeout(() => {
          console.log("Updated profile state after reload:", {
            avatarUrl,
            profileAvatarUrl: profile.avatar_url
          });
        }, 500);
        
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError("Avatar uploaded but failed to update profile. Please try saving changes manually.");
        setProfileUpdated(true);
      }
      
    } catch (err: any) {
      console.error("Error uploading avatar:", err);
      setError(`Failed to upload avatar: ${err.message}`);
    } finally {
      setIsUploading(false);
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen relative px-2 py-3">
      {/* Add custom styles */}
      <style>{settingsStyles}</style>
      <style>{avatarStyles}</style>
      
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <SettingsIcon className="text-[#42ae84]" />
            Settings
          </h1>
          </div>
          
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          {/* Sidebar */}
          <div className="lg:col-span-3">
            <div className="settings-card mb-6">
              <div className="settings-card-header">
                <h2 className="text-sm font-semibold text-[#42ae84]">NAVIGATION</h2>
            </div>
              <div className="settings-card-body">
                <div className="space-y-1">
            <button 
              onClick={() => handleTabClick('account')}
                    className={`w-full text-left px-3 py-2 rounded-md flex items-center gap-2 ${
                      activeTab === 'account' 
                        ? 'bg-[#42ae84]/20 text-[#42ae84]' 
                        : 'hover:bg-[#1c2c2c] text-gray-300'
                    }`}
                  >
                    <User size={16} />
                    <span>Account Settings</span>
            </button>
            <button 
              className="w-full text-left px-3 py-2 rounded-md flex items-center justify-between text-gray-500 cursor-not-allowed opacity-70"
            >
              <div className="flex items-center gap-2">
                <Bell size={16} />
                <span>Notification Preferences</span>
              </div>
              <Lock size={14} />
            </button>
                </div>
              </div>
          </div>
          
            {/* Wallet Status Card */}
            <div className="settings-card mb-6">
              <div className="settings-card-header">
                <h2 className="text-sm font-semibold text-[#42ae84]">WALLET STATUS</h2>
                    </div>
              <div className="settings-card-body">
                <div className="section-padding">
                  {walletAddress ? (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-[#42ae84]/10 flex items-center justify-center border border-[#42ae84]/20">
                          <Wallet size={16} className="text-[#42ae84]" />
                  </div>
                        <div>
                          <p className="text-sm font-medium text-white">Connected Wallet</p>
                          <p className="text-xs text-gray-400">
                            {walletProvider === 'phantom' ? 'Phantom' : walletProvider === 'solflare' ? 'Solflare' : 'Unknown'}
                          </p>
                </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-2 rounded-md bg-[#1c2c2c] border border-gray-700/40">
                        <div className="text-xs text-gray-300 font-medium truncate max-w-[180px]">
                          {formatWalletAddress(walletAddress)}
                  </div>
                  <div className="flex gap-1">
                    <button 
                      onClick={() => copyToClipboard('wallet', walletAddress)}
                            className="p-1 hover:bg-[rgba(66,174,132,0.2)] transition-colors rounded-md bg-[rgba(66,174,132,0.1)]"
                            title={copied === 'wallet' ? "Copied!" : "Copy address"}
                          >
                            {copied === 'wallet' ? <CheckCircle size={12} className="text-[#42ae84]" /> : <Copy size={12} className="text-gray-300" />}
                    </button>
                    <a 
                            href={`https://solscan.io/address/${walletAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                            className="p-1 hover:bg-white/10 rounded-md bg-[#1c2c2c]"
                      title="View on Solscan"
                    >
                            <ExternalLink size={12} className="text-gray-300" />
                    </a>
                  </div>
                </div>
              </div>
                  ) : (
                    <div className="flex flex-col items-center py-3 text-center">
                      <div className="w-12 h-12 rounded-full bg-[#42ae84]/10 flex items-center justify-center mb-3 border border-[#42ae84]/20">
                        <Wallet size={24} className="text-[#42ae84]" />
                      </div>
                      <p className="text-sm text-gray-300 mb-3">
                        Connect your wallet to access account settings
                      </p>
                      <button 
                        onClick={handleConnectWallet}
                        className="settings-button-primary text-sm py-2 px-4 flex items-center gap-2"
                      >
                        <Wallet size={14} />
                        Connect Wallet
                      </button>
            </div>
          )}
                </div>
              </div>
            </div>
            
            {/* Security Card */}
            <div className="settings-card">
              <div className="settings-card-header">
                <h2 className="text-sm font-semibold text-[#c19411]">SECURITY</h2>
              </div>
              <div className="settings-card-body">
                <div className="section-padding">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-[#c19411]/10 flex items-center justify-center border border-[#c19411]/20">
                      <Shield size={16} className="text-[#c19411]" />
                </div>
                <div className="flex-1">
                      <p className="text-sm font-medium">Account Protection</p>
                      <p className="text-xs text-gray-400">Advanced security features</p>
                    </div>
                    <Lock size={16} className="text-gray-500" />
                  </div>
                  
                  <div className="bg-[#1c2c2c] p-3 rounded-md border border-[#c19411]/20">
                    <p className="text-xs text-gray-300 mb-3">
                      Enable advanced security features to protect your account and assets.
                    </p>
                    <button 
                      className="w-full py-2 text-xs font-medium bg-[#c19411]/10 text-gray-500 border border-[#c19411]/30 rounded-md flex items-center justify-center gap-2 opacity-70 cursor-not-allowed"
                      disabled={true}
                    >
                      <Shield size={14} />
                      Security Settings
                      <Lock size={12} className="ml-1" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
          {/* Main Content */}
          <div className="lg:col-span-9">
            {/* Account Settings Tab */}
            {activeTab === 'account' && (
              <div className="settings-card">
                <div className="settings-card-header flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-[#42ae84]">ACCOUNT SETTINGS</h2>
                  {saveSuccess && (
                    <div className="flex items-center gap-1 text-xs text-[#42ae84] bg-[#42ae84]/10 py-1 px-2 rounded-md border border-[#42ae84]/20">
                      <CheckCircle size={12} />
                      <span>Saved successfully</span>
                    </div>
                  )}
                </div>
                <div className="settings-card-body">
                  {!walletAddress ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400 mb-4">Please connect your wallet to manage your account settings</p>
              <button 
                        onClick={handleConnectWallet}
                        className="settings-button-primary text-sm py-2 px-4 flex items-center gap-2 mx-auto"
              >
                        <Wallet size={14} />
                        Connect Wallet
              </button>
                    </div>
                  ) : (
                    <>
                      {error && (
                        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-2 rounded-md mb-2 mx-3 mt-3 flex items-start gap-2">
                          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                          <p className="text-xs">{error}</p>
            </div>
          )}
          
                      <div className="grid gap-2">
                        {/* Profile Section with Avatar Upload and Display Name */}
                        <div className="flex flex-col md:flex-row gap-2 items-start">
                          {/* Avatar Upload Component */}
                          <div 
                            className={`avatar-upload ${isDragging ? 'avatar-drop-active' : ''}`}
                            onClick={handleAvatarClick}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                          >
                            <input 
                              type="file" 
                              ref={fileInputRef}
                              className="avatar-upload-input" 
                              accept="image/jpeg,image/png,image/gif"
                              onChange={handleFileChange}
                            />
                            <div className="avatar-preview">
                              {avatarUrl ? (
                                <img src={avatarUrl} alt="Profile avatar" />
                              ) : (
                                <div className="avatar-placeholder">
                                  <Upload size={30} className="text-gray-400 mb-1" />
                                  <div className="avatar-placeholder-info">
                                    JPG, PNG, GIF<br/>
                                    Max 2MB
            </div>
              </div>
            )}
          </div>
                            <div className="avatar-upload-overlay">
                              <Upload size={20} className="text-white mb-1" />
                              <span className="text-xs text-white">Upload Image</span>
              </div>
                            {isUploading && (
                              <div className="avatar-upload-progress">
                                <span className="text-sm mb-1">{uploadProgress}%</span>
                                <Loader size={20} className="animate-spin" />
            </div>
                            )}
                  </div>
                          
                          {/* Display Name Field */}
                          <div className="flex-1 compact-form">
                            <div className="flex justify-between items-center mb-1">
                              <label className="block text-sm font-medium text-gray-300">Display Name</label>
                              {!canChangeName && timeLeft && (
                                <div className="flex items-center text-xs text-yellow-500 bg-yellow-500/10 px-1.5 py-0.5 rounded border border-yellow-500/20">
                                  <Clock size={10} className="mr-1" />
                                  <span>Change in {formatTimeRemaining(timeLeft)}</span>
                  </div>
                              )}
                </div>
                            <div className="relative">
                          <input
                            type="text"
                            value={profile.display_name || ''}
                            onChange={(e) => handleProfileChange('display_name', e.target.value)}
                                className={`settings-input ${!canChangeName ? 'pr-8 opacity-70' : ''}`}
                                placeholder="How you'll appear to others"
                                disabled={!canChangeName}
                              />
                              {!canChangeName && (
                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-yellow-500">
                                  <Lock size={14} />
                                </div>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              {canChangeName 
                                ? "You can change your display name once per week." 
                                : `Can't change until ${nextChangeDate?.toLocaleString()}`
                              }
                            </p>
                          </div>
                        </div>
                        
                        {/* Email field */}
                        <div className="compact-form">
                          <label className="block text-sm font-medium text-gray-300 mb-1">Email Address</label>
                          <input
                            type="email"
                            value={profile.email || ''}
                            onChange={(e) => handleProfileChange('email', e.target.value)}
                            className="settings-input"
                            placeholder="Your email address"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            We'll never share your email.
                          </p>
                        </div>
                        
                        {/* Social media fields */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          {/* X (Twitter) field */}
                          <div className="compact-form">
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                              <div className="flex items-center gap-1">
                                {/* X logo */}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white">
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" fill="currentColor" />
                                </svg>
                                <span>X</span>
                        </div>
                          </label>
                            <input
                              type="text"
                              value={profile.twitter_handle || ''}
                              onChange={(e) => handleProfileChange('twitter_handle', e.target.value)}
                              className="settings-input"
                              placeholder="@username"
                            />
                        </div>
                        
                          {/* Telegram field */}
                          <div className="compact-form">
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                              <div className="flex items-center gap-1">
                                {/* Telegram logo */}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#0088cc]">
                                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-1.515 7.143c-.112.54-.412.674-.836.42l-2.302-1.7-1.112 1.072c-.125.125-.23.23-.468.23l.167-2.378 4.332-3.913c.188-.168-.041-.262-.292-.094L7.48 12.77l-2.295-.714c-.5-.157-.508-.5.104-.74l8.964-3.454c.431-.157.81.104.662.386z" fill="currentColor" />
                              </svg>
                                <span>Telegram</span>
                            </div>
                            </label>
                            <input
                              type="text"
                              value={profile.telegram_handle || ''}
                              onChange={(e) => handleProfileChange('telegram_handle', e.target.value)}
                              className="settings-input"
                              placeholder="@username"
                            />
                        </div>
                        
                          {/* Discord field */}
                          <div className="compact-form">
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                              <div className="flex items-center gap-1">
                                {/* Discord logo */}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#5865F2]">
                                  <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.39-.444.898-.608 1.297a19.97 19.97 0 0 0-5.93 0 12.57 12.57 0 0 0-.617-1.297.077.077 0 0 0-.079-.036A20.2 20.2 0 0 0 3.5 4.492C3.487 4.498 3.477 4.507 3.47 4.52.533 9.046-.32 13.456.099 17.801c.004.044.032.085.071.106 1.866 1.369 3.678 2.202 5.455 2.752.046.014.095.003.126-.036.65-.91 1.231-1.872 1.731-2.884.025-.052 0-.11-.06-.137a13.38 13.38 0 0 1-1.959-.938.077.077 0 0 1-.008-.127c.132-.099.264-.202.39-.309a.076.076 0 0 1 .08-.01c4.031 1.84 8.393 1.84 12.375 0a.077.077 0 0 1 .081.009c.127.107.259.21.391.309.043.035.05.106-.006.128a12.5 12.5 0 0 1-1.96.937.076.076 0 0 0-.059.138c.507 1.011 1.087 1.973 1.73 2.883.031.04.08.05.127.036 1.782-.55 3.594-1.383 5.46-2.752a.077.077 0 0 0 .072-.106c-.505-5.039-.858-9.371-3.487-13.281a.06.06 0 0 0-.031-.028ZM8.02 15.278c-1.077 0-1.972-.993-1.972-2.22 0-1.227.87-2.22 1.972-2.22 1.11 0 1.991.999 1.98 2.22 0 1.227-.87 2.22-1.98 2.22Zm7.314 0c-1.077 0-1.971-.993-1.971-2.22 0-1.227.87-2.22 1.971-2.22 1.111 0 1.992.999 1.98 2.22 0 1.227-.858 2.22-1.98 2.22Z" fill="currentColor"/>
                                </svg>
                                <span>Discord</span>
                            </div>
                            </label>
                            <input
                              type="text"
                              value={profile.discord_handle || ''}
                              onChange={(e) => handleProfileChange('discord_handle', e.target.value)}
                              className="settings-input"
                              placeholder="username#0000"
                            />
                          </div>
                        </div>
                        
                        {/* Bio field - ensure not resizable */}
                        <div className="section-padding">
                          <label className="block text-sm font-medium text-gray-300 mb-1">Bio</label>
                          <div className="relative">
                            <textarea
                              value={profile.bio || ''}
                              onChange={(e) => handleProfileChange('bio', e.target.value)}
                              className="settings-input min-h-[160px] resize-none"
                              placeholder="Tell us about yourself..."
                              maxLength={500}
                              style={{resize: 'none'}}
                            />
                            <div className={`char-counter ${bioCharCount > 400 ? 'warning' : ''}`}>
                              {bioCharCount}/500
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Save button */}
                      <div className="flex justify-end mt-2 pb-3">
                          <button 
                            onClick={handleSaveProfile}
                          disabled={isSaving || !profileUpdated}
                          className={`
                            flex items-center gap-2 text-sm px-4 py-1.5 rounded-md
                            ${isSaving || !profileUpdated 
                              ? 'bg-gray-700/50 text-gray-400 cursor-not-allowed' 
                              : 'settings-button-primary'
                            }
                          `}
                        >
                          {isSaving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                              </>
                            )}
                        </div>
                  </div>
                )}
            
            {/* Notification Preferences Tab */}
            {activeTab === 'notifications' && (
              <div className="settings-card">
                <div className="settings-card-header flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-[#42ae84]">NOTIFICATION PREFERENCES</h2>
                  {saveSuccess && (
                    <div className="flex items-center gap-1 text-xs text-[#42ae84] bg-[#42ae84]/10 py-1 px-2 rounded-md border border-[#42ae84]/20">
                      <CheckCircle size={12} />
                      <span>Saved successfully</span>
                  </div>
                  )}
                  </div>
                <div className="settings-card-body">
                {!walletAddress ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400 mb-4">Please connect your wallet to manage your notification preferences</p>
                      <button 
                        onClick={handleConnectWallet}
                        className="settings-button-primary text-sm py-2 px-4 flex items-center gap-2 mx-auto"
                      >
                        <Wallet size={14} />
                        Connect Wallet
                      </button>
                    </div>
                  ) : (
                    <>
                      {error && (
                        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-md mb-4 flex items-start gap-2">
                          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                          <p className="text-sm">{error}</p>
                        </div>
                      )}
                      
                      <div className="space-y-6">
                        <div>
                          <h3 className="text-sm font-medium mb-3 text-[#42ae84]">Activity Notifications</h3>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                          <div>
                                <p className="text-sm font-medium">Trade Notifications</p>
                                <p className="text-xs text-gray-400">Receive updates about your trades and orders</p>
                          </div>
                              <label className="settings-toggle">
                            <input 
                              type="checkbox" 
                              checked={notificationPrefs.trade_notifications}
                              onChange={() => handleNotificationToggle('trade_notifications')}
                            />
                                <span className="toggle-slider"></span>
                          </label>
                        </div>

                            <div className="flex items-center justify-between">
                          <div>
                                <p className="text-sm font-medium">Price Alerts</p>
                                <p className="text-xs text-gray-400">Get notified when assets reach your target price</p>
                          </div>
                              <label className="settings-toggle">
                            <input 
                              type="checkbox"
                              checked={notificationPrefs.price_alerts}
                              onChange={() => handleNotificationToggle('price_alerts')}
                            />
                                <span className="toggle-slider"></span>
                          </label>
                        </div>
                        
                            <div className="flex items-center justify-between">
                          <div>
                                <p className="text-sm font-medium">Wallet Activity</p>
                                <p className="text-xs text-gray-400">Receive updates about transactions in your wallet</p>
                          </div>
                              <label className="settings-toggle">
                            <input 
                              type="checkbox"
                              checked={notificationPrefs.wallet_activity}
                              onChange={() => handleNotificationToggle('wallet_activity')}
                            />
                                <span className="toggle-slider"></span>
                          </label>
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="text-sm font-medium mb-3 text-[#42ae84]">Security Notifications</h3>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                          <div>
                                <p className="text-sm font-medium">Gas Price Alerts</p>
                                <p className="text-xs text-gray-400">Get notified when gas prices are high or low</p>
                          </div>
                              <label className="settings-toggle">
                            <input 
                              type="checkbox"
                              checked={notificationPrefs.gas_price_alerts}
                              onChange={() => handleNotificationToggle('gas_price_alerts')}
                            />
                                <span className="toggle-slider"></span>
                          </label>
                        </div>
                        
                            <div className="flex items-center justify-between">
                          <div>
                                <p className="text-sm font-medium">Smart Contract Alerts</p>
                                <p className="text-xs text-gray-400">Receive notifications about contracts you interact with</p>
                          </div>
                              <label className="settings-toggle">
                            <input 
                              type="checkbox"
                              checked={notificationPrefs.smart_contract_alerts}
                              onChange={() => handleNotificationToggle('smart_contract_alerts')}
                            />
                                <span className="toggle-slider"></span>
                          </label>
                        </div>
                        </div>
                      </div>
                    
                        <div>
                          <h3 className="text-sm font-medium mb-3 text-[#42ae84]">Notification Delivery</h3>
                    <div className="space-y-4">
                            <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium">In-App Notifications</p>
                                <p className="text-xs text-gray-400">Show notifications within the app</p>
                            </div>
                              <label className="settings-toggle">
                              <input 
                                type="checkbox"
                                checked={notificationPrefs.in_app_notifications}
                                onChange={() => handleNotificationToggle('in_app_notifications')}
                              />
                                <span className="toggle-slider"></span>
                            </label>
                          </div>
                          
                            <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium">Email Notifications</p>
                                <p className="text-xs text-gray-400">Send notifications to your email</p>
                            </div>
                              <label className="settings-toggle">
                              <input 
                                type="checkbox"
                                checked={notificationPrefs.email_notifications}
                                onChange={() => handleNotificationToggle('email_notifications')}
                              />
                                <span className="toggle-slider"></span>
                            </label>
                          </div>
                          
                            <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium">Push Notifications</p>
                                <p className="text-xs text-gray-400">Enable browser push notifications</p>
                            </div>
                              <label className="settings-toggle">
                              <input 
                                type="checkbox"
                                checked={notificationPrefs.push_notifications}
                                onChange={() => handleNotificationToggle('push_notifications')}
                              />
                                <span className="toggle-slider"></span>
                            </label>
                            </div>
                          </div>
                          </div>
                          
                        <div className="flex justify-end pt-4 border-t border-gray-700/40">
                            <button 
                              onClick={handleSaveNotificationPreferences}
                              disabled={isSaving}
                            className={`
                              flex items-center gap-2 text-sm px-4 py-2 rounded-md
                              ${isSaving 
                                ? 'bg-gray-700/50 text-gray-400 cursor-not-allowed' 
                                : 'settings-button-primary'
                              }
                            `}
                          >
                            {isSaving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
                            {isSaving ? 'Saving...' : 'Save Preferences'}
                            </button>
                          </div>
                        </div>
                    </>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;